// src/components/admin/AdminAuth.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Tabs,
  Typography,
  message,
  Row,
  Col,
  Space,
  Divider,
} from "antd";
import { useNavigate } from "react-router-dom";
import { MailOutlined, LockOutlined, UserOutlined, SafetyOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export default function AdminAuth({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const loginFormRef = useRef(null);
  const signupFormRef = useRef(null);

  const apiBase = (
  window.__APP_CONFIG__?.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:8000"
).replace(/\/$/, ""); // remove trailing slash if any
  const signupEndpoint = apiBase + "/admin_signup.php";
  const loginEndpoint = apiBase + "/admin_login.php";

  // redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      navigate("/admin/approvals", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Signup
  const handleSignup = async (values) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", values.name);
      fd.append("email", values.email);
      fd.append("password", values.password);
      if (values.secret) fd.append("secret", values.secret);

      const res = await fetch(signupEndpoint, { method: "POST", body: fd });
      const txt = await res.text();
      let data = null;
      try { data = txt ? JSON.parse(txt) : null; } catch (e) { throw new Error(txt || "Unexpected response from signup"); }

      if (!res.ok) throw new Error(data?.error || "Signup failed");
      messageApi.success("Signup successful. You can now login.");
    } catch (err) {
      console.error("signup error", err);
      messageApi.error(err?.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  // Login
  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(loginEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: values.email, password: values.password }),
      });

      const raw = await res.text();
      let data = null;
      try { data = raw ? JSON.parse(raw) : null; } catch (e) { throw new Error(raw || "Invalid JSON response from server"); }

      if (!res.ok || !data || !data.ok) throw new Error(data?.error || "Login failed");

      const tokenCandidate = data.token;
      if (!tokenCandidate || typeof tokenCandidate !== "string") throw new Error("Server returned token in unexpected format");
      const parts = tokenCandidate.split(".");
      if (parts.length !== 3) throw new Error("Invalid token received from server");

      // persist token and notify parent
      localStorage.setItem("admin_token", tokenCandidate);
      if (typeof onLogin === "function") onLogin(tokenCandidate);

      messageApi.success("Login successful");
      navigate("/admin/approvals", { replace: true });
    } catch (err) {
      console.error("login error", err);
      messageApi.error(err?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // focus email in login when switching to login tab
  const onTabChange = (key) => {
    if (key === "login") {
      setTimeout(() => {
        try {
          // try focusing email input in the login form (may vary by AntD version)
          const el = document.querySelector('input[name="email"]');
          el?.focus?.();
        } catch (e) {}
      }, 60);
    }
  };

  return (
    <div className="admin-auth-page">
      {contextHolder}

      <style>{`
        .admin-auth-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 36px 20px;
          background: linear-gradient(180deg, #f7f8fc 0%, #ffffff 100%);
        }

        .admin-auth-container { width: 100%; max-width: 1100px; }

        .admin-auth-card {
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 18px 60px rgba(7,18,38,0.08);
          border: 1px solid rgba(15,23,42,0.04);
        }

        .auth-left {
          background: linear-gradient(180deg, rgba(109,40,217,0.06), rgba(109,40,217,0.02));
          padding: 28px;
          min-height: 360px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .auth-logo { width: 68px; height: 68px; object-fit: contain; border-radius: 8px; background: #fff; display:block; padding:8px; box-shadow: inset 0 -6px 0 rgba(109,40,217,0.06); }

        .auth-title { margin-top: 12px; margin-bottom: 6px; color: #0f172a; }
        .auth-desc { color: rgba(15,23,42,0.65); max-width: 320px; }

        .auth-illustration {
          margin-top: 18px;
          width: 100%;
          max-width: 360px;
          opacity: 0.95;
        }

        .auth-cta { margin-top: 18px; color: rgba(15,23,42,0.7); }

        .auth-right { padding: 28px; }

        .auth-tabs .ant-tabs-nav { margin-bottom: 18px; }
        .auth-form .ant-form-item { margin-bottom: 14px; }
        .auth-small { display:block; color: rgba(15,23,42,0.55); margin-bottom:6px; }
        .auth-link { color: rgba(15,23,42,0.8); }

        /* responsive: stack on small screens */
        @media (max-width: 768px) {
          .auth-left { padding: 20px; min-height: auto; text-align: center; }
          .auth-right { padding: 20px; }
        }
      `}</style>

      <div className="admin-auth-container">
        <Card className="admin-auth-card" bordered={false}>
          <Row gutter={0} wrap={true}>
            {/* left panel */}
            <Col xs={24} sm={24} md={10} className="auth-left">
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
               
                <div>
                  <Title level={4} style={{ margin: 0 }}>PSG IAS — Admin Console</Title>
                 
                </div>
              </div>

              {/* small illustrative SVG */}
              <div style={{ marginTop: 20 }}>
                <svg className="auth-illustration" viewBox="0 0 600 260" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <rect x="0" y="0" width="600" height="260" rx="12" fill="transparent"/>
                  <g transform="translate(20,20)" fill="none" stroke="rgba(109,40,217,0.24)" strokeWidth="2">
                    <path d="M0 120 C80 40 160 40 240 120 S400 200 480 120" strokeLinecap="round" />
                    <circle cx="80" cy="120" r="10" fill="rgba(109,40,217,0.12)" stroke="rgba(109,40,217,0.32)"/>
                    <circle cx="240" cy="120" r="14" fill="rgba(109,40,217,0.18)" stroke="rgba(109,40,217,0.36)"/>
                    <circle cx="400" cy="120" r="9" fill="rgba(109,40,217,0.10)" stroke="rgba(109,40,217,0.28)"/>
                  </g>
                </svg>
              </div>

          
            </Col>

            {/* right panel */}
            <Col xs={24} sm={24} md={14} className="auth-right">
              <Tabs defaultActiveKey="login" centered onChange={onTabChange} className="auth-tabs">
                <TabPane tab="Login" key="login">
                  <Form layout="vertical" onFinish={handleLogin} ref={loginFormRef} className="auth-form">
                    <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
                      <Input size="large" prefix={<MailOutlined />} placeholder="you@company.com" name="email" />
                    </Form.Item>

                    <Form.Item name="password" label="Password" rules={[{ required: true }]}>
                      <Input.Password size="large" prefix={<LockOutlined />} placeholder="Enter your password" />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 6 }}>
                      <Button type="primary" htmlType="submit" loading={loading} block size="large">
                        Login
                      </Button>
                    </Form.Item>
                  </Form>
                </TabPane>

                <TabPane tab="Sign up" key="signup">
                  <Form layout="vertical" onFinish={handleSignup} ref={signupFormRef} className="auth-form">
                    <Form.Item name="name" label="Full name" rules={[{ required: true }]}>
                      <Input size="large" prefix={<UserOutlined />} placeholder="Your full name" />
                    </Form.Item>

                    <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
                      <Input size="large" prefix={<MailOutlined />} placeholder="you@company.com" />
                    </Form.Item>

                    <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
                      <Input.Password size="large" prefix={<LockOutlined />} placeholder="Choose a strong password" />
                    </Form.Item>

                    <Form.Item name="secret" label="Admin code (optional)">
                      <Input size="large" prefix={<SafetyOutlined />} placeholder="Optional verification code" />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 6 }}>
                      <Button type="default" htmlType="submit" loading={loading} block size="large">
                        Create admin
                      </Button>
                    </Form.Item>
                  </Form>
                </TabPane>
              </Tabs>

              <Divider style={{ marginTop: 6 }} />

              <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 6 }}>
                <Button type="link" onClick={() => window.location.href = "/"}>Back to site</Button>
               
              </div>
            </Col>
          </Row>
        </Card>
      </div>
    </div>
  );
}
